
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Course {
  id: string;
  title: string;
  description: string | null;
  hours: number;
  event_type: "live" | "self_paced";
  status: "draft" | "published" | "archived" | "cancelled";
  max_participants: number | null;
  venue_details: string | null;
  start_date: string | null;
  end_date: string | null;
  target_audience: string;
  subject_area: string;
  category: string;
  state_requirements: Record<string, any>;
}

export interface Registration {
  id: string;
  course_id: string;
  status: string;
  waitlist_position: number | null;
  verified_at: string | null;
}

export interface CourseRequirement {
  name: string;
  hours: number;
}

export const useCoursesData = (profileId: string | undefined) => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [eventType, setEventType] = useState("all");
  const [sortOrder, setSortOrder] = useState<string>("date-asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  
  const ITEMS_PER_PAGE = 9;
  const currentDate = new Date().toISOString();

  useEffect(() => {
    const savedFavorites = localStorage.getItem('favoriteCourses');
    if (savedFavorites) {
      setFavoriteIds(JSON.parse(savedFavorites));
    }
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, eventType, sortOrder]);

  // Fetch courses
  const { data: courses = [], isLoading: isLoadingCourses } = useQuery({
    queryKey: ['courses', eventType],
    queryFn: async () => {
      console.log('Fetching courses with filter:', { eventType });
      let query = supabase
        .from('courses')
        .select('*')
        .eq('status', 'published');

      if (eventType !== "all") {
        query = query.eq('event_type', eventType);
      }

      if (eventType === "live" || eventType === "all") {
        query = query.or(`event_type.neq.live,and(event_type.eq.live,start_date.gte.${currentDate})`);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching courses:', error);
        toast({
          title: "Error",
          description: "Failed to load courses. Please try again.",
          variant: "destructive",
        });
        return [];
      }

      return data as Course[];
    },
    refetchOnWindowFocus: true,
  });

  // Fetch user registrations
  const { data: userRegistrations = [], refetch: refetchRegistrations } = useQuery({
    queryKey: ['registrations', profileId],
    queryFn: async () => {
      if (!profileId) return [];
      
      console.log('Fetching registrations for user:', profileId);
      const { data, error } = await supabase
        .from('course_registrations')
        .select('*')
        .eq('user_id', profileId);

      if (error) {
        console.error('Error fetching registrations:', error);
        return [];
      }

      return data as Registration[];
    },
    enabled: !!profileId,
  });

  // Filter valid courses (future live events or self-paced)
  const validCourses = courses.filter(course => {
    if (course.event_type === 'self_paced') return true;
    if (course.event_type === 'live' && course.start_date) {
      return new Date(course.start_date) >= new Date();
    }
    return false;
  });

  // Apply search and sort
  const filteredCourses = validCourses
    .filter(course => {
      if (!searchQuery) return true;
      
      const query = searchQuery.toLowerCase();
      return (
        course.title.toLowerCase().includes(query) ||
        (course.description && course.description.toLowerCase().includes(query)) ||
        course.subject_area.toLowerCase().includes(query) ||
        course.category.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      switch (sortOrder) {
        case 'date-asc':
          return (a.start_date || '9999') > (b.start_date || '9999') ? 1 : -1;
        case 'date-desc':
          return (a.start_date || '0000') < (b.start_date || '0000') ? 1 : -1;
        case 'title-asc':
          return a.title.localeCompare(b.title);
        case 'title-desc':
          return b.title.localeCompare(a.title);
        case 'hours-asc':
          return Number(a.hours) - Number(b.hours);
        case 'hours-desc':
          return Number(b.hours) - Number(a.hours);
        default:
          return 0;
      }
    });

  // Handle pagination
  const indexOfLastCourse = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstCourse = indexOfLastCourse - ITEMS_PER_PAGE;
  const currentCourses = filteredCourses.slice(indexOfFirstCourse, indexOfLastCourse);
  const totalPages = Math.ceil(filteredCourses.length / ITEMS_PER_PAGE);

  // Utils for registrations
  const isRegistered = (courseId: string) => {
    return userRegistrations.some(reg => reg.course_id === courseId);
  };

  const getRegistrationStatus = (courseId: string) => {
    const registration = userRegistrations.find(reg => reg.course_id === courseId);
    return registration?.status || null;
  };

  const isVerified = (courseId: string) => {
    const registration = userRegistrations.find(reg => reg.course_id === courseId);
    return registration?.verified_at !== null;
  };

  // Favorites handling
  const saveToFavorites = (courseId: string) => {
    const newFavorites = favoriteIds.includes(courseId)
      ? favoriteIds.filter(id => id !== courseId)
      : [...favoriteIds, courseId];
    
    setFavoriteIds(newFavorites);
    localStorage.setItem('favoriteCourses', JSON.stringify(newFavorites));
    
    toast({
      title: favoriteIds.includes(courseId) ? "Removed from favorites" : "Added to favorites",
      description: favoriteIds.includes(courseId) 
        ? "Course has been removed from your saved items" 
        : "Course has been saved to your favorites",
    });
  };

  // Format state requirements for display
  const getStateRequirementsList = (course: Course): CourseRequirement[] => {
    if (!course.state_requirements) return [];
    
    const requirements: CourseRequirement[] = [];
    
    const mainCategories = [
      'behavioralIntervention',
      'readingPreparation',
      'keyWarningSigns',
      'suicidePrevention',
      'englishLearners',
      'americanIndianHistory'
    ];
    
    mainCategories.forEach(key => {
      if (course.state_requirements[key]) {
        const hours = parseFloat(course.state_requirements[`${key}Hours`] || '0');
        if (hours > 0) {
          requirements.push({
            name: formatRequirementName(key),
            hours
          });
        }
      }
    });
    
    if (course.state_requirements.culturalCompetency?.enabled) {
      const subcategories = [
        'racial',
        'religion',
        'systemicRacism',
        'genderIdentity',
        'sexualOrientation',
        'languageDiversity',
        'disabilities'
      ];
      
      subcategories.forEach(key => {
        if (course.state_requirements.culturalCompetency[key]) {
          const hours = parseFloat(course.state_requirements.culturalCompetency[`${key}Hours`] || '0');
          if (hours > 0) {
            requirements.push({
              name: formatRequirementName(key),
              hours
            });
          }
        }
      });
    }
    
    return requirements;
  };

  // Handle enrollment
  const handleEnroll = async (courseId: string) => {
    if (!profileId) {
      toast({
        title: "Error",
        description: "Please log in to enroll in courses.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('course_registrations')
        .insert([
          {
            course_id: courseId,
            user_id: profileId,
            status: 'registered'
          }
        ]);

      if (error) throw error;

      await refetchRegistrations();

      toast({
        title: "Success",
        description: "Successfully enrolled in the course!",
      });
    } catch (error: any) {
      console.error('Error enrolling in course:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to enroll in the course. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle cancellation
  const handleCancelRegistration = async (courseId: string) => {
    try {
      const registration = userRegistrations.find(reg => reg.course_id === courseId);
      if (!registration) return;

      const { error } = await supabase
        .from('course_registrations')
        .delete()
        .eq('id', registration.id);

      if (error) throw error;

      await refetchRegistrations();

      toast({
        title: "Success",
        description: "Successfully cancelled your registration.",
      });
    } catch (error: any) {
      console.error('Error cancelling registration:', error);
      toast({
        title: "Error",
        description: "Failed to cancel registration. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Helper function to format requirement names
  const formatRequirementName = (key: string): string => {
    const nameMap: Record<string, string> = {
      'behavioralIntervention': 'Positive Behavioral Intervention',
      'readingPreparation': 'Reading Preparation',
      'keyWarningSigns': 'Key Warning Signs of Mental Illness',
      'suicidePrevention': 'Suicide Prevention',
      'englishLearners': 'English Learners',
      'americanIndianHistory': 'American Indian History & Culture',
      'racial': 'Racial, Cultural & Socioeconomic Groups',
      'religion': 'Religion',
      'systemicRacism': 'Systemic Racism',
      'genderIdentity': 'Gender Identity',
      'sexualOrientation': 'Sexual Orientation',
      'languageDiversity': 'Language Diversity',
      'disabilities': 'Individuals with Disabilities'
    };
    
    return nameMap[key] || key;
  };

  // Stats calculations
  const stats = {
    totalCourses: validCourses.length,
    liveCourses: validCourses.filter(c => c.event_type === 'live').length,
    selfPacedCourses: validCourses.filter(c => c.event_type === 'self_paced').length,
    enrolledCourses: userRegistrations.length
  };

  return {
    // Data
    courses: currentCourses,
    filteredCourses,
    favoriteIds,
    userRegistrations,
    stats,
    
    // Pagination
    currentPage,
    totalPages,
    indexOfFirstCourse,
    indexOfLastCourse,
    
    // Filters
    searchQuery,
    eventType,
    sortOrder,
    
    // Loading states
    isLoadingCourses,
    
    // Actions
    setCurrentPage,
    setSearchQuery,
    setEventType,
    setSortOrder,
    saveToFavorites,
    handleEnroll,
    handleCancelRegistration,
    
    // Utils
    isRegistered,
    getRegistrationStatus,
    isVerified,
    getStateRequirementsList
  };
};
